import {
  GetCSVProgressResp,
  GetCSVReportsReq,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/platform/platform.pb';
import dayjs from 'dayjs';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { getUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { MetricId } from '../constants';
import { getDownloadStatusKey, getSaveConfigKey } from '../utils';

type ReportingFilters = {
  endDate?: string;
  locationIds?: string[];
  startDate?: string;
};

export type LastSavedConfig = {
  fileName?: string;
  filters?: ReportingFilters;
  selectedMetrices?: MetricId[];
  timePeriodKey?: string | null;
};

type LastDownloadStatus = {
  downloadUrl: string;
  fileName: string;
  isPreparingDownload?: boolean;
  mediaHash: string;
};

interface AnalyticsReportingStore {
  downloadStatus?: LastDownloadStatus;
  fileName?: string;
  filters?: ReportingFilters;
  selectedMetrices?: MetricId[];
  intervalId?: NodeJS.Timeout | null;

  onDownloadStatusChange: (status: LastDownloadStatus, selectedLocationIds: string[]) => void;
  onFileNameChange: (fileName: string) => void;
  onFiltersChange: (filters: ReportingFilters) => void;
  onInitializeStore: (selectedLocationIds: string[]) => void;
  onSelectedMetricesBatchChange: (metricIds: MetricId[], isSelected?: boolean) => void;
  onSelectedMetricesChange: (metricId: MetricId, isSelected?: boolean) => void;
  resetStore: () => void;

  _checkStatus: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

const initialState: Partial<AnalyticsReportingStore> = {
  downloadStatus: undefined,
  fileName: 'Analytics Overview', // This is default file name to be saved, no translation needed
  filters: undefined,
  selectedMetrices: ['phone'],
  intervalId: null,
};

export const downloadProgressStatusCheckApi = async (payload: GetCSVReportsReq) => {
  const response = await http.post<GetCSVProgressResp>('/analytics-reporting/platform/platform/csv/progress', payload);
  return response;
};

const useAnalyticsReportingStore = createStoreWithSubscribe<AnalyticsReportingStore>(
  (set, get) => ({
    ...initialState,

    onFileNameChange: (fileName: string) => {
      set({ fileName });
    },

    onFiltersChange: (filters: ReportingFilters) => {
      const currentFilters = get().filters || {};
      set({ filters: { ...currentFilters, ...filters } });
    },

    onInitializeStore: (selectedLocationIds) => {
      // Read last used config from local storage
      const userId = getUser()?.userID;
      if (!userId || selectedLocationIds.length === 0) {
        return;
      }

      const lastUsedConfig = localStorage.getItem(
        getSaveConfigKey({
          glsLocationIds: selectedLocationIds,
          userId,
        })
      );

      if (lastUsedConfig) {
        const config: Partial<AnalyticsReportingStore> = {};

        try {
          const parsedConfig: LastSavedConfig = JSON.parse(lastUsedConfig);

          if (parsedConfig) {
            if (parsedConfig.fileName) {
              config.fileName = parsedConfig.fileName;
            }

            if (parsedConfig.filters) {
              const timePeriod = {
                startDate: parsedConfig.filters.startDate,
                endDate: parsedConfig.filters.endDate,
              };

              // If time period key is present, use it to set start and end dates
              if (parsedConfig.timePeriodKey && parsedConfig.timePeriodKey in AnalyticsHooks.defaultTimePeriods) {
                const { value } =
                  AnalyticsHooks.defaultTimePeriods[
                    parsedConfig.timePeriodKey as keyof typeof AnalyticsHooks.defaultTimePeriods
                  ];

                if (value) {
                  timePeriod.startDate = value.startDate;
                  timePeriod.endDate = value.endDate;
                }
              }

              config.filters = {
                ...parsedConfig.filters,
                ...timePeriod,
              };
            }

            if (parsedConfig.selectedMetrices) {
              config.selectedMetrices = parsedConfig.selectedMetrices;
            }

            set({ ...config });
          }
        } catch (e) {
          // Do nothing if error in reading/parsing the data
        }
      }

      const lastDownloadStatus = localStorage.getItem(
        getDownloadStatusKey({
          glsLocationIds: selectedLocationIds,
          userId,
        })
      );

      if (lastDownloadStatus) {
        try {
          const parsedStatus: LastDownloadStatus = JSON.parse(lastDownloadStatus);

          if (parsedStatus && typeof parsedStatus.isPreparingDownload === 'boolean') {
            set({ downloadStatus: parsedStatus });

            // If the user returns and a download was still preparing, restart the polling.
            if (parsedStatus.isPreparingDownload) {
              get().startPolling();
            }
          }
        } catch (e) {
          // Do nothing if error in reading/parsing the data
        }
      }
    },

    onDownloadStatusChange: (downloadStatus, selectedLocationIds) => {
      const userId = getUser()?.userID;
      if (!userId) {
        return;
      }

      localStorage.setItem(
        getDownloadStatusKey({
          glsLocationIds: selectedLocationIds,
          userId,
        }),
        JSON.stringify(downloadStatus)
      );

      set({ downloadStatus });

      // This is the central controller.
      if (downloadStatus.isPreparingDownload) {
        get().startPolling();
      } else {
        get().stopPolling();
      }
    },

    onSelectedMetricesBatchChange: (metricIds: MetricId[], isSelected?: boolean) => {
      const currentSelected = get().selectedMetrices || [];
      let updatedSelected: MetricId[];

      if (isSelected) {
        const newSelected = metricIds.filter((id) => !currentSelected.includes(id));
        updatedSelected = [...currentSelected, ...newSelected];
      } else {
        updatedSelected = currentSelected.filter((id) => !metricIds.includes(id));
      }

      set({ selectedMetrices: updatedSelected });
    },

    onSelectedMetricesChange: (metricId: MetricId, isSelected?: boolean) => {
      const currentSelected = get().selectedMetrices || [];
      let updatedSelected: MetricId[];

      if (isSelected) {
        if (currentSelected.includes(metricId)) {
          updatedSelected = currentSelected;
        } else {
          updatedSelected = [...currentSelected, metricId];
        }
      } else {
        updatedSelected = currentSelected.filter((id) => id !== metricId);
      }

      set({ selectedMetrices: updatedSelected });
    },

    resetStore: () => {
      get().stopPolling();
      set({ ...initialState });
    },

    _checkStatus: async () => {
      const { downloadStatus, filters, selectedMetrices } = get();

      if (!downloadStatus?.mediaHash || !downloadStatus.isPreparingDownload) {
        get().stopPolling();
        return;
      }

      try {
        const { status } = await downloadProgressStatusCheckApi({
          filters: {
            startTime: dayjs(filters?.startDate).format('YYYY-MM-DDT00:00:00.000'),
            endTime: dayjs(filters?.endDate).format('YYYY-MM-DDT23:59:59.999'),
          },
          format: 'csv',
          locationId: filters?.locationIds,
          metricName: selectedMetrices,
        });

        if (status === 'completed') {
          get().onDownloadStatusChange(
            {
              ...downloadStatus,
              isPreparingDownload: false,
            },
            filters?.locationIds || []
          );
        }
      } catch (error) {
        console.error('Analytics reporting download status check failed:', error);
        get().onDownloadStatusChange(
          {
            ...downloadStatus,
            isPreparingDownload: false,
          },
          filters?.locationIds || []
        );
      }
    },

    startPolling: () => {
      if (get().intervalId) {
        return;
      }

      get()._checkStatus();

      const newIntervalId = setInterval(get()._checkStatus, 5000);
      set({ intervalId: newIntervalId });
    },

    stopPolling: () => {
      const { intervalId } = get();

      if (intervalId) {
        clearInterval(intervalId);
        set({ intervalId: null });
      }
    },
  }),
  {
    name: 'AnalyticsReportingStore',
    trace: true,
  }
);

export const useAnalyticsReportingShallowStore =
  createShallowStore<AnalyticsReportingStore>(useAnalyticsReportingStore);
